import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Plus, Trash2, Edit, Save, X, ChevronDown, ChevronUp, GripVertical, Eye, EyeOff, Link as LinkIcon, Image, FileText, Sparkles, Loader, Clock, ExternalLink, Video, FolderOpen } from 'lucide-react';
import ImageUploadField from './ImageUploadField';
import FocalPointPicker from './FocalPointPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Collection Image Picker Component
const CollectionImagePicker = ({ collections, suggestedImages, currentValue, onSelect }) => {
  const [selectedCollection, setSelectedCollection] = useState('suggested');
  const [collectionImages, setCollectionImages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load images when collection changes
  useEffect(() => {
    if (selectedCollection === 'suggested') {
      setCollectionImages(suggestedImages || []);
      return;
    }
    
    const loadCollectionImages = async () => {
      if (!selectedCollection) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/content/collections/${selectedCollection}`);
        const col = await res.json();
        const images = [];
        
        // Add cover image
        if (col.cover_image_url) {
          images.push({ url: col.cover_image_url, source: `${col.couple} - Cover` });
        }
        
        // Add all collection images
        (col.images || []).forEach((img, idx) => {
          const imgUrl = typeof img === 'string' ? img : img.url;
          if (imgUrl) {
            images.push({ url: imgUrl, source: `${col.couple} - Photo ${idx + 1}` });
          }
        });
        
        setCollectionImages(images);
      } catch (err) {
        console.error('Error loading collection:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadCollectionImages();
  }, [selectedCollection, suggestedImages]);

  return (
    <div className="space-y-3">
      {/* Collection selector */}
      <select
        value={selectedCollection}
        onChange={(e) => setSelectedCollection(e.target.value)}
        className="w-full px-3 py-2 border rounded text-sm"
      >
        <option value="suggested">AI Suggested Images ({suggestedImages?.length || 0})</option>
        <option value="" disabled>──────────</option>
        {collections.map(col => (
          <option key={col.id} value={col.id}>
            {col.couple} - {col.location} ({(col.images?.length || 0) + 1} images)
          </option>
        ))}
      </select>
      
      {/* Image grid */}
      {loading ? (
        <div className="text-center py-8">
          <Loader size={20} className="animate-spin mx-auto text-gray-400" />
        </div>
      ) : collectionImages.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-8">No images available in this collection</p>
      ) : (
        <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto p-1">
          {collectionImages.map((img, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onSelect(img.url, img.source)}
              className={`relative aspect-square rounded overflow-hidden border-2 transition-all hover:opacity-80 ${
                currentValue === img.url ? 'border-black ring-2 ring-black' : 'border-transparent'
              }`}
            >
              <img
                src={img.url}
                alt={img.source || ''}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {currentValue === img.url && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <span className="text-white text-xs font-medium">Selected</span>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      
      {/* Current selection preview */}
      {currentValue && (
        <div className="flex items-center gap-3 p-2 bg-green-50 rounded">
          <img src={currentValue} alt="" className="w-12 h-12 object-cover rounded" />
          <span className="text-xs text-green-700 flex-1 truncate">Selected image</span>
          <button
            type="button"
            onClick={() => onSelect('', '')}
            className="text-red-500 text-xs hover:text-red-700"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

const BLOCK_TYPES = [
  { id: 'text', label: 'Text', icon: '📝' },
  { id: 'image', label: 'Image', icon: '🖼️' },
  { id: 'video', label: 'Video', icon: '🎬' },
  { id: 'gif', label: 'GIF', icon: '🎞️' },
  { id: 'quote', label: 'Quote', icon: '💬' },
  { id: 'excerpt', label: 'Excerpt/Highlight', icon: '✨' },
  { id: 'gallery', label: 'Gallery', icon: '🎨' },
  { id: 'collection_link', label: 'Collection Link', icon: '🔗' }
];

const BlogPanel = () => {
  const [posts, setPosts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);

  // AI Generator state
  const [genTopic, setGenTopic] = useState('');
  const [genTags, setGenTags] = useState([]);
  const [genInstructions, setGenInstructions] = useState('');
  const [genTone, setGenTone] = useState('professional');
  const [allTags, setAllTags] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [genMessage, setGenMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = api.getToken();
      const [postsRes, collectionsRes, tagsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/blog/posts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/content/collections`),
        fetch(`${API_URL}/api/blog/all-tags`).catch(() => ({ json: () => ({ tags: [] }) }))
      ]);
      
      const postsData = await postsRes.json();
      const collectionsData = await collectionsRes.json();
      const tagsData = await tagsRes.json();
      
      setPosts(postsData || []);
      setCollections(collectionsData || []);
      setAllTags(tagsData.tags || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPost({
      slug: '',
      title: '',
      excerpt: '',
      featured_image_url: '',
      featured_image_alt: '',
      content_blocks: [],
      seo_title: '',
      seo_description: '',
      seo_keywords: [],
      categories: [],
      tags: [],
      linked_collection_id: '',
      is_published: false,
      is_featured: false
    });
    setShowEditor(true);
  };

  const handleEdit = async (post) => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/admin/blog/posts/${post.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const fullPost = await res.json();
      setEditingPost(fullPost);
      setShowEditor(true);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load post' });
    }
  };

  const handleSave = async () => {
    if (!editingPost.title) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    setSaving(true);
    try {
      const token = api.getToken();
      const isNew = !editingPost.id;
      
      // Generate slug if not provided
      const postData = {
        ...editingPost,
        slug: editingPost.slug || editingPost.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      };
      
      const url = isNew 
        ? `${API_URL}/api/admin/blog/posts`
        : `${API_URL}/api/admin/blog/posts/${editingPost.id}`;
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: `Post ${isNew ? 'created' : 'updated'} successfully` });
        setShowEditor(false);
        setEditingPost(null);
        fetchData();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save post' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save post' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/admin/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Post deleted' });
        fetchData();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete post' });
    }
  };

  const handlePublish = async (postId, publish) => {
    try {
      const token = api.getToken();
      const endpoint = publish ? 'publish' : 'unpublish';
      await fetch(`${API_URL}/api/admin/blog/posts/${postId}/${endpoint}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
      setMessage({ type: 'success', text: `Post ${publish ? 'published' : 'unpublished'}` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update post' });
    }
  };

  // Block management
  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      order: editingPost.content_blocks.length,
      heading: '',
      content: '',
      image_url: '',
      image_caption: '',
      image_alt: '',
      video_url: '',
      gif_url: '',
      quote_text: '',
      quote_author: '',
      excerpt_text: '',
      collection_id: '',
      collection_link_text: 'View Full Gallery',
      gallery_images: [],
      layout: 'full'
    };
    setEditingPost({
      ...editingPost,
      content_blocks: [...editingPost.content_blocks, newBlock]
    });
  };

  const updateBlock = (blockId, updates) => {
    setEditingPost({
      ...editingPost,
      content_blocks: editingPost.content_blocks.map(b =>
        b.id === blockId ? { ...b, ...updates } : b
      )
    });
  };

  const removeBlock = (blockId) => {
    setEditingPost({
      ...editingPost,
      content_blocks: editingPost.content_blocks.filter(b => b.id !== blockId)
    });
  };

  const moveBlock = (blockId, direction) => {
    const blocks = [...editingPost.content_blocks];
    const index = blocks.findIndex(b => b.id === blockId);
    if (direction === 'up' && index > 0) {
      [blocks[index], blocks[index - 1]] = [blocks[index - 1], blocks[index]];
    } else if (direction === 'down' && index < blocks.length - 1) {
      [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
    }
    blocks.forEach((b, i) => b.order = i);
    setEditingPost({ ...editingPost, content_blocks: blocks });
  };

  const renderBlockEditor = (block, index) => {
    return (
      <div key={block.id} className="border rounded-lg p-4 mb-4 bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GripVertical size={16} className="text-gray-400" />
            <span className="font-medium capitalize">{block.type}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => moveBlock(block.id, 'up')} className="p-1 hover:bg-gray-200 rounded" disabled={index === 0}>
              <ChevronUp size={16} />
            </button>
            <button onClick={() => moveBlock(block.id, 'down')} className="p-1 hover:bg-gray-200 rounded" disabled={index === editingPost.content_blocks.length - 1}>
              <ChevronDown size={16} />
            </button>
            <button onClick={() => removeBlock(block.id)} className="p-1 hover:bg-red-100 text-red-500 rounded">
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {/* Block-specific fields */}
        {block.type === 'text' && (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Heading (optional)"
              value={block.heading || ''}
              onChange={(e) => updateBlock(block.id, { heading: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
            <textarea
              placeholder="Content"
              value={block.content || ''}
              onChange={(e) => updateBlock(block.id, { content: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        )}

        {block.type === 'image' && (
          <div className="space-y-3">
            {/* Tab selector for upload vs collection picker */}
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => updateBlock(block.id, { _imageSource: 'upload' })}
                className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 ${(!block._imageSource || block._imageSource === 'upload') ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <Plus size={12} /> Upload New
              </button>
              <button
                type="button"
                onClick={() => updateBlock(block.id, { _imageSource: 'collection' })}
                className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 ${block._imageSource === 'collection' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <FolderOpen size={12} /> From Collections
              </button>
            </div>
            
            {/* Upload mode */}
            {(!block._imageSource || block._imageSource === 'upload') && (
              <ImageUploadField
                value={block.image_url || ''}
                onChange={(url) => updateBlock(block.id, { image_url: url })}
                folder="blog"
                label=""
              />
            )}
            
            {/* Collection picker mode */}
            {block._imageSource === 'collection' && (
              <CollectionImagePicker 
                collections={collections}
                suggestedImages={editingPost.suggested_collection_images || []}
                currentValue={block.image_url}
                onSelect={(url, source) => updateBlock(block.id, { image_url: url, image_caption: source || block.image_caption })}
              />
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Caption"
                value={block.image_caption || ''}
                onChange={(e) => updateBlock(block.id, { image_caption: e.target.value })}
                className="px-3 py-2 border rounded text-sm"
              />
              <input
                type="text"
                placeholder="Alt text (SEO)"
                value={block.image_alt || ''}
                onChange={(e) => updateBlock(block.id, { image_alt: e.target.value })}
                className="px-3 py-2 border rounded text-sm"
              />
            </div>
            {/* Focal Points for Image */}
            {block.image_url && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <FocalPointPicker
                  imageUrl={block.image_url}
                  focalPoint={block.image_focal_desktop || { x: 50, y: 50 }}
                  onChange={(fp) => updateBlock(block.id, { image_focal_desktop: fp })}
                  label="Desktop Focus"
                />
                <FocalPointPicker
                  imageUrl={block.image_url}
                  focalPoint={block.image_focal_mobile || { x: 50, y: 50 }}
                  onChange={(fp) => updateBlock(block.id, { image_focal_mobile: fp })}
                  label="Mobile Focus"
                />
              </div>
            )}
          </div>
        )}

        {block.type === 'video' && (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Video URL"
              value={block.video_url || ''}
              onChange={(e) => updateBlock(block.id, { video_url: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        )}

        {block.type === 'gif' && (
          <div className="space-y-3">
            <ImageUploadField
              value={block.gif_url || ''}
              onChange={(url) => updateBlock(block.id, { gif_url: url })}
              folder="blog"
              label=""
              accept="image/gif,image/*"
            />
            <input
              type="text"
              placeholder="Caption (optional)"
              value={block.image_caption || ''}
              onChange={(e) => updateBlock(block.id, { image_caption: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
        )}

        {block.type === 'quote' && (
          <div className="space-y-3">
            <textarea
              placeholder="Quote text"
              value={block.quote_text || ''}
              onChange={(e) => updateBlock(block.id, { quote_text: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded"
            />
            <input
              type="text"
              placeholder="Author (optional)"
              value={block.quote_author || ''}
              onChange={(e) => updateBlock(block.id, { quote_author: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
        )}

        {block.type === 'excerpt' && (
          <div className="space-y-3">
            <textarea
              placeholder="Highlighted excerpt text"
              value={block.excerpt_text || ''}
              onChange={(e) => updateBlock(block.id, { excerpt_text: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        )}

        {block.type === 'collection_link' && (
          <div className="space-y-3">
            <select
              value={block.collection_id || ''}
              onChange={(e) => updateBlock(block.id, { collection_id: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Select a collection</option>
              {collections.map(col => (
                <option key={col.id} value={col.id}>{col.couple} - {col.location}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Link text"
              value={block.collection_link_text || 'View Full Gallery'}
              onChange={(e) => updateBlock(block.id, { collection_link_text: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
        )}

        {/* Layout selector */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Layout:</label>
            <select
              value={block.layout || 'full'}
              onChange={(e) => updateBlock(block.id, { layout: e.target.value })}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value="full">Full Width</option>
              <option value="wide">Wide</option>
              <option value="centered">Centered</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // AI Blog Generator
  if (showGenerator) {
    const handleGenerate = async () => {
      if (!genTopic.trim()) {
        setGenMessage('Please enter a topic');
        return;
      }
      setGenerating(true);
      setGenMessage('Generating blog post with AI... This may take 30-60 seconds.');
      try {
        const token = api.getToken();
        const res = await fetch(`${API_URL}/api/blog/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            topic: genTopic,
            tags: genTags,
            additional_instructions: genInstructions,
            tone: genTone,
          }),
        });
        const data = await res.json();
        if (data.success) {
          setGenMessage('Blog post generated! Opening editor...');
          setShowGenerator(false);
          setGenTopic('');
          setGenTags([]);
          setGenInstructions('');
          await fetchData();
          // Open the newly generated post for editing
          if (data.post) {
            setEditingPost(data.post);
            setShowEditor(true);
          }
        } else {
          setGenMessage('Error: ' + (data.message || 'Generation failed'));
        }
      } catch (err) {
        setGenMessage('Error: ' + err.message);
      } finally {
        setGenerating(false);
      }
    };

    const toggleTag = (tag) => {
      setGenTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Sparkles size={20} className="text-amber-500" /> AI Blog Generator
            </h2>
            <p className="text-sm text-gray-500 mt-1">Enter a topic and let AI write a full SEO-optimized blog post</p>
          </div>
          <button onClick={() => setShowGenerator(false)} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {genMessage && (
          <div className={`p-3 rounded flex items-center gap-2 ${genMessage.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
            {generating && <Loader size={16} className="animate-spin" />}
            {genMessage}
          </div>
        )}

        {/* Topic */}
        <div className="border rounded-lg p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Blog Topic / Title *</label>
            <input
              type="text"
              value={genTopic}
              onChange={(e) => setGenTopic(e.target.value)}
              className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-base"
              placeholder="e.g., Top 10 Pakistani Wedding Photography Tips in Toronto"
              data-testid="ai-blog-topic"
              disabled={generating}
            />
          </div>

          {/* Tags Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Select Tags (pulls matching images & videos)</label>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  disabled={generating}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                    genTags.includes(tag) 
                      ? 'bg-black text-white border-black' 
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                  data-testid={`ai-tag-${tag}`}
                >
                  {tag}
                </button>
              ))}
              {allTags.length === 0 && (
                <p className="text-xs text-gray-400">No tags found. Add tags to your collections first.</p>
              )}
            </div>
            {/* Custom tag input */}
            <input
              type="text"
              className="w-full mt-2 px-3 py-1.5 border rounded text-sm"
              placeholder="Or type custom tags (comma separated)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newTags = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  setGenTags(prev => [...new Set([...prev, ...newTags])]);
                  e.target.value = '';
                }
              }}
              disabled={generating}
            />
          </div>

          {/* Tone */}
          <div>
            <label className="block text-sm font-medium mb-2">Writing Tone</label>
            <div className="flex gap-2">
              {['professional', 'warm', 'editorial', 'storytelling'].map(tone => (
                <button
                  key={tone}
                  onClick={() => setGenTone(tone)}
                  disabled={generating}
                  className={`px-4 py-2 text-xs rounded-lg border capitalize transition-all ${
                    genTone === tone ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-300'
                  }`}
                >
                  {tone}
                </button>
              ))}
            </div>
          </div>

          {/* Additional Instructions */}
          <div>
            <label className="block text-sm font-medium mb-1">Additional Instructions (optional)</label>
            <textarea
              value={genInstructions}
              onChange={(e) => setGenInstructions(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              rows={3}
              placeholder="e.g., Focus on mehndi ceremonies, mention our venue partnerships, include pricing hints..."
              disabled={generating}
            />
          </div>
        </div>

        {/* Selected tags summary */}
        {genTags.length > 0 && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1.5">AI will pull images from collections tagged with:</p>
            <div className="flex flex-wrap gap-1.5">
              {genTags.map(tag => (
                <span key={tag} className="px-2 py-0.5 bg-black text-white text-xs rounded-full">
                  {tag}
                  <button onClick={() => toggleTag(tag)} className="ml-1 hover:text-red-300">&times;</button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Generate Button */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowGenerator(false)}
            disabled={generating}
            className="px-6 py-2 border rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || !genTopic.trim()}
            className="px-8 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 flex items-center gap-2 font-medium"
            data-testid="ai-generate-btn"
          >
            {generating ? (
              <>
                <Loader size={16} className="animate-spin" /> Generating...
              </>
            ) : (
              <>
                <Sparkles size={16} /> Generate Blog Post
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Post Editor
  if (showEditor && editingPost) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {editingPost.id ? 'Edit Post' : 'New Blog Post'}
          </h2>
          <button onClick={() => { setShowEditor(false); setEditingPost(null); }} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {message.text && (
          <div className={`p-3 rounded ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {message.text}
          </div>
        )}

        {/* Basic Info */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-medium">Post Details</h3>
          <div>
            <label className="block text-sm font-medium mb-1">Title *</label>
            <input
              type="text"
              value={editingPost.title}
              onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder="Your blog post title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL Slug</label>
            <input
              type="text"
              value={editingPost.slug}
              onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              className="w-full px-3 py-2 border rounded"
              placeholder="auto-generated-from-title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Excerpt</label>
            <textarea
              value={editingPost.excerpt}
              onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded"
              placeholder="Brief description for blog listing"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Featured Image</label>
              {/* Tab selector for upload vs collection picker */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setEditingPost({ ...editingPost, _featuredImageSource: 'upload' })}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 ${(!editingPost._featuredImageSource || editingPost._featuredImageSource === 'upload') ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <Plus size={12} /> Upload
                </button>
                <button
                  type="button"
                  onClick={() => setEditingPost({ ...editingPost, _featuredImageSource: 'collection' })}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 ${editingPost._featuredImageSource === 'collection' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <FolderOpen size={12} /> From Collections
                </button>
              </div>
              
              {(!editingPost._featuredImageSource || editingPost._featuredImageSource === 'upload') ? (
                <ImageUploadField
                  value={editingPost.featured_image_url}
                  onChange={(url) => setEditingPost({ ...editingPost, featured_image_url: url })}
                  folder="blog"
                  label=""
                />
              ) : (
                <CollectionImagePicker 
                  collections={collections}
                  suggestedImages={editingPost.suggested_collection_images || []}
                  currentValue={editingPost.featured_image_url}
                  onSelect={(url, source) => setEditingPost({ ...editingPost, featured_image_url: url, featured_image_alt: source || editingPost.featured_image_alt })}
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image Alt Text</label>
              <input
                type="text"
                value={editingPost.featured_image_alt}
                onChange={(e) => setEditingPost({ ...editingPost, featured_image_alt: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Link to Collection (for client stories)</label>
            <select
              value={editingPost.linked_collection_id || ''}
              onChange={(e) => setEditingPost({ ...editingPost, linked_collection_id: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">No linked collection</option>
              {collections.map(col => (
                <option key={col.id} value={col.id}>{col.couple} - {col.location}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Categories (comma-separated)</label>
              <input
                type="text"
                value={(editingPost.categories || []).join(', ')}
                onChange={(e) => setEditingPost({ ...editingPost, categories: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                className="w-full px-3 py-2 border rounded"
                placeholder="Wedding, Real Story"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
              <input
                type="text"
                value={(editingPost.tags || []).join(', ')}
                onChange={(e) => setEditingPost({ ...editingPost, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                className="w-full px-3 py-2 border rounded"
                placeholder="toronto, summer, garden"
              />
            </div>
          </div>
        </div>

        {/* Content Blocks */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Content Blocks</h3>
            <div className="flex gap-2 flex-wrap">
              {BLOCK_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => addBlock(type.id)}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-100"
                  title={type.label}
                >
                  {type.icon}
                </button>
              ))}
            </div>
          </div>

          {editingPost.content_blocks.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Add content blocks to build your post</p>
          ) : (
            editingPost.content_blocks
              .sort((a, b) => a.order - b.order)
              .map((block, index) => renderBlockEditor(block, index))
          )}
        </div>

        {/* Linked Videos Section - Auto-populated by AI */}
        {(editingPost.linked_videos && editingPost.linked_videos.length > 0) && (
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <Video size={16} className="text-purple-500" />
                Linked Videos ({editingPost.linked_videos.length})
              </h3>
              <span className="text-xs text-gray-400">Auto-linked from your film collection</span>
            </div>
            <p className="text-xs text-gray-500">These videos were automatically linked based on your tags. Click "Add to Post" to insert a video block.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {editingPost.linked_videos.map((video, idx) => (
                <div key={idx} className="border rounded-lg overflow-hidden bg-gray-50">
                  {video.thumbnail && (
                    <img src={video.thumbnail} alt={video.title} className="w-full h-24 object-cover" />
                  )}
                  <div className="p-2">
                    <p className="text-xs font-medium truncate">{video.title || 'Untitled Video'}</p>
                    <button
                      type="button"
                      onClick={() => {
                        addBlock('video');
                        // Update the last added block with this video URL
                        setTimeout(() => {
                          const blocks = [...editingPost.content_blocks];
                          const lastBlock = blocks[blocks.length - 1];
                          if (lastBlock && lastBlock.type === 'video') {
                            lastBlock.video_url = video.url;
                          }
                          setEditingPost({ ...editingPost, content_blocks: blocks });
                        }, 100);
                      }}
                      className="mt-2 w-full px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded hover:bg-purple-200"
                    >
                      Add to Post
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Collection Images - For quick access */}
        {(editingPost.suggested_collection_images && editingPost.suggested_collection_images.length > 0) && (
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <Image size={16} className="text-blue-500" />
                Suggested Images ({editingPost.suggested_collection_images.length})
              </h3>
              <span className="text-xs text-gray-400">From your collections matching selected tags</span>
            </div>
            <p className="text-xs text-gray-500">Click any image to add it as a new image block in your post.</p>
            <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto">
              {editingPost.suggested_collection_images.slice(0, 18).map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    addBlock('image');
                    setTimeout(() => {
                      const blocks = [...editingPost.content_blocks];
                      const lastBlock = blocks[blocks.length - 1];
                      if (lastBlock && lastBlock.type === 'image') {
                        lastBlock.image_url = img.url;
                        lastBlock.image_caption = img.source || '';
                      }
                      setEditingPost({ ...editingPost, content_blocks: blocks });
                    }, 100);
                  }}
                  className="aspect-square rounded overflow-hidden border hover:border-blue-500 hover:ring-2 hover:ring-blue-200 transition-all"
                  title={img.source || 'Add to post'}
                >
                  <img src={img.url} alt={img.source || ''} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SEO */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-medium">SEO Settings</h3>
          <div>
            <div className="flex justify-between mb-1">
              <label className="text-xs text-gray-500">SEO Title</label>
              <span className={`text-xs ${(editingPost.seo_title || '').length > 60 ? 'text-red-500' : 'text-gray-400'}`}>{(editingPost.seo_title || '').length}/60</span>
            </div>
            <input
              type="text"
              placeholder="SEO Title (50-60 chars ideal)"
              value={editingPost.seo_title || ''}
              onChange={(e) => setEditingPost({ ...editingPost, seo_title: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <label className="text-xs text-gray-500">Meta Description</label>
              <span className={`text-xs ${(editingPost.seo_description || '').length > 160 ? 'text-red-500' : 'text-gray-400'}`}>{(editingPost.seo_description || '').length}/160</span>
            </div>
            <textarea
              placeholder="Meta description (150-160 chars ideal)"
              value={editingPost.seo_description || ''}
              onChange={(e) => setEditingPost({ ...editingPost, seo_description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">SEO Keywords</label>
            <input
              type="text"
              placeholder="keyword1, keyword2, keyword3 (comma separated)"
              value={(editingPost.seo_keywords || []).join(', ')}
              onChange={(e) => setEditingPost({ ...editingPost, seo_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              className="w-full px-3 py-2 border rounded"
            />
            {(editingPost.seo_keywords || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {editingPost.seo_keywords.map((kw, i) => (
                  <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">{kw}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Publish Options */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-medium">Publishing</h3>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editingPost.is_featured}
                onChange={(e) => setEditingPost({ ...editingPost, is_featured: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm">Featured Post</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editingPost.is_published}
                onChange={(e) => setEditingPost({ ...editingPost, is_published: e.target.checked, scheduled_at: e.target.checked ? null : editingPost.scheduled_at })}
                className="w-4 h-4"
              />
              <span className="text-sm">Published</span>
            </label>
          </div>

          {/* Schedule */}
          {!editingPost.is_published && (
            <div className="bg-gray-50 p-3 rounded-lg space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Clock size={14} />
                Schedule for later
              </label>
              <input
                type="datetime-local"
                value={editingPost.scheduled_at ? new Date(editingPost.scheduled_at).toISOString().slice(0, 16) : ''}
                onChange={(e) => setEditingPost({ ...editingPost, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                className="w-full px-3 py-2 border rounded text-sm"
                data-testid="schedule-datetime"
              />
              {editingPost.scheduled_at && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-blue-600">
                    Will auto-publish on {new Date(editingPost.scheduled_at).toLocaleString()}
                  </p>
                  <button onClick={() => setEditingPost({ ...editingPost, scheduled_at: null })} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                </div>
              )}
            </div>
          )}

          {/* Status badge */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Status:</span>
            {editingPost.is_published ? (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Published</span>
            ) : editingPost.scheduled_at ? (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium flex items-center gap-1">
                <Clock size={10} /> Scheduled
              </span>
            ) : (
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">Draft</span>
            )}
          </div>
        </div>

        {/* Save & Preview Buttons */}
        <div className="flex justify-between gap-3">
          <div className="flex gap-2">
            {/* Preview button */}
            {editingPost.id && (
              <button
                onClick={() => {
                  const token = api.getToken();
                  window.open(`${API_URL}/api/blog/preview/${editingPost.id}?token=${token}`, '_blank');
                }}
                className="px-4 py-2 border rounded hover:bg-gray-50 flex items-center gap-2 text-sm"
                data-testid="preview-blog-btn"
              >
                <ExternalLink size={14} /> Preview JSON
              </button>
            )}
            {editingPost.id && editingPost.slug && (
              <button
                onClick={() => {
                  const previewUrl = `${window.location.origin}/blog/${editingPost.slug}?preview=${editingPost.id}&token=${api.getToken()}`;
                  window.open(previewUrl, '_blank');
                }}
                className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 flex items-center gap-2 text-sm"
                data-testid="preview-live-btn"
              >
                <Eye size={14} /> Preview Live
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => { setShowEditor(false); setEditingPost(null); }}
              className="px-6 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
            >
              <Save size={16} />
              {saving ? 'Saving...' : editingPost.scheduled_at ? 'Save & Schedule' : 'Save Post'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Posts List
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Blog Posts</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your blog content</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowGenerator(true)}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded hover:from-amber-600 hover:to-orange-600 flex items-center gap-2"
            data-testid="ai-generate-open-btn"
          >
            <Sparkles size={16} />
            Generate with AI
          </button>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 flex items-center gap-2"
          >
            <Plus size={16} />
            New Post
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`p-3 rounded ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message.text}
        </div>
      )}

      {posts.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-400 mb-4">No blog posts yet</p>
          <button onClick={handleCreate} className="text-blue-600 hover:underline">
            Create your first post
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div key={post.id} className="border rounded-lg p-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-4">
                {post.featured_image_url ? (
                  <img src={post.featured_image_url} alt="" className="w-16 h-12 object-cover rounded" />
                ) : (
                  <div className="w-16 h-12 bg-gray-200 rounded flex items-center justify-center">
                    <Image size={20} className="text-gray-400" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className={`font-medium ${!post.is_published && 'text-gray-400'}`}>{post.title}</h3>
                    {post.is_featured && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] rounded uppercase">Featured</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">/blog/{post.slug}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs rounded ${
                  post.is_published ? 'bg-green-100 text-green-700' : 
                  post.scheduled_at ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {post.is_published ? 'Published' : post.scheduled_at ? 'Scheduled' : 'Draft'}
                </span>
                {post.scheduled_at && !post.is_published && (
                  <span className="text-[10px] text-blue-500">{new Date(post.scheduled_at).toLocaleDateString()}</span>
                )}
                <button
                  onClick={() => handlePublish(post.id, !post.is_published)}
                  className={`p-2 rounded ${post.is_published ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                  title={post.is_published ? 'Unpublish' : 'Publish'}
                >
                  {post.is_published ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
                <button
                  onClick={() => handleEdit(post)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Edit size={18} />
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogPanel;
